import React, { useState } from 'react';
import { API_BASE } from '../../config/api';

export const ExcelUploadModal = ({ entity, entityLabel, isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        alert('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setResult(null);
      setErrors([]);
      setShowErrors(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        alert('Please log in first');
        return;
      }

      const response = await fetch(`${API_BASE}/api/admin/templates/${entity}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        alert('Session expired. Please log in again.');
        window.location.href = '/admin/login';
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entity}_import_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template. Please try again.');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setResult(null);
      setErrors([]);
      setShowErrors(false);

      const token = localStorage.getItem('admin_token');
      if (!token) {
        alert('Please log in first');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('dry_run', dryRun.toString());

      const response = await fetch(`${API_BASE}/api/admin/import/${entity}?dry_run=${dryRun}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        alert('Session expired. Please log in again.');
        window.location.href = '/admin/login';
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }

      setResult(data);
      setErrors(data.errors || []);

      if (data.success && !dryRun && data.failed === 0) {
        // Refresh data if successful and not dry run
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setErrors([]);
    setShowErrors(false);
    setDryRun(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Bulk Import {entityLabel}</h2>
              <p className="text-blue-100 text-sm mt-1">Upload Excel file to import or update {entityLabel.toLowerCase()}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Download Template */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-[#1E40AF] mb-1">Step 1: Download Template</h3>
                <p className="text-sm text-gray-600">Download the Excel template with validation rules and dropdowns</p>
              </div>
              <button
                onClick={handleDownloadTemplate}
                className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Template
              </button>
            </div>
          </div>

          {/* Upload File */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">Step 2: Upload Filled Template</h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1E40AF] transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="excel-file-input"
              />
              <label
                htmlFor="excel-file-input"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {file ? (
                  <div className="text-center">
                    <p className="text-[#1E40AF] font-semibold">{file.name}</p>
                    <p className="text-sm text-gray-500 mt-1">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-600 font-medium">Click to select Excel file</p>
                    <p className="text-sm text-gray-400 mt-1">.xlsx or .xls format only</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Dry Run Option */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="dry-run"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="w-5 h-5 text-[#1E40AF] border-gray-300 rounded focus:ring-[#1E40AF]"
            />
            <label htmlFor="dry-run" className="text-gray-700 cursor-pointer">
              <span className="font-semibold">Dry Run (Validation Only)</span>
              <span className="text-sm text-gray-500 ml-2">- Validate file without saving to database</span>
            </label>
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Uploading...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {dryRun ? 'Validate File' : 'Upload & Import'}
              </>
            )}
          </button>

          {/* Result Summary */}
          {result && (
            <div className={`rounded-lg p-4 border-2 ${
              result.success && result.failed === 0
                ? 'bg-green-50 border-green-200'
                : result.success
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <h4 className="font-semibold mb-2">
                {result.success && result.failed === 0 ? '✅ Import Successful' : result.success ? '⚠️ Import Completed with Errors' : '❌ Import Failed'}
              </h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Created:</span>
                  <span className="ml-2 font-semibold text-green-600">{result.created || 0}</span>
                </div>
                <div>
                  <span className="text-gray-600">Updated:</span>
                  <span className="ml-2 font-semibold text-blue-600">{result.updated || 0}</span>
                </div>
                <div>
                  <span className="text-gray-600">Failed:</span>
                  <span className="ml-2 font-semibold text-red-600">{result.failed || 0}</span>
                </div>
              </div>
              {result.dry_run && (
                <p className="text-sm text-blue-600 mt-2">ℹ️ This was a dry run. No data was saved.</p>
              )}
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="border-2 border-red-200 rounded-lg">
              <button
                onClick={() => setShowErrors(!showErrors)}
                className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 text-left flex items-center justify-between rounded-t-lg transition-colors"
              >
                <span className="font-semibold text-red-700">
                  {errors.length} Error{errors.length !== 1 ? 's' : ''} Found
                </span>
                <svg
                  className={`w-5 h-5 text-red-700 transform transition-transform ${showErrors ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showErrors && (
                <div className="p-4 max-h-64 overflow-y-auto space-y-2">
                  {errors.map((error, index) => (
                    <div key={index} className="text-sm bg-red-50 border border-red-200 rounded p-3">
                      <span className="font-semibold text-red-700">Row {error.row}:</span>
                      <span className="text-red-600 ml-2">
                        {error.field && <span className="font-medium">{error.field}: </span>}
                        {error.error}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 font-semibold rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
